import { getEHVersion } from '@mx/util/get-eh-version';
import { getSystem } from '@mx/util/get-system';
import Vue from 'vue';
import { toast } from '@mx/vix';

export interface IPosition {
  x: number;
  y: number;
}

export type IScrollHandler = (position: IPosition) => void;
export type Constructor<T = {}> = new (...args: any[]) => T;

export interface IEHBridge {
  ehVersion: string;
  ehConfig: IEHConfig;
  configEH(config: Partial<IEHConfig>): void;
  setTitle(title: string): void;
  showNavigationBar(): void;
  ehShow(): void;
  closeWebView(): void;
  openWebView(url: string, useEH?: boolean): void;
  addScrollHandler(handler: IScrollHandler): void;
  removeScrollHandler(handler: IScrollHandler): void;
  readyForTransPage(
    step: number,
    useAnimation?: boolean,
    cb?: () => void
  ): void;
  transPageGo(step: number, complete?: () => void): void;
}

export interface IEHConfig {
  processBar: boolean;
  hideNavigationBar: boolean;
  statusBarColorLight: boolean;
  scrollViewBounce: boolean;
  scrollViewBottomBounce: boolean;
  scrollViewTopBounce: boolean;
  scrollViewBackgroundColor: string;
}

export function mixinEHBridge<TBase extends Constructor>(
  Base: TBase
): Constructor<IEHBridge> & TBase {
  return class EHBridge extends Base {
    readonly ehVersion: string;

    private system: string;
    private scrollHandlers: IScrollHandler[] = [];
    private pEhConfig = {} as IEHConfig;
    private webViewOpening = false;

    constructor(...args: any[]) {
      super(...args);
      this.system = getSystem();
      this.ehVersion = getEHVersion();
      this.configEH({
        processBar: false,
        hideNavigationBar: false,
        statusBarColorLight: false,
        scrollViewBounce: true,
        scrollViewBottomBounce: true,
        scrollViewTopBounce: true,
        scrollViewBackgroundColor: '#FFFFFF'
      });
    }

    get ehConfig() {
      return this.pEhConfig;
    }

    configEH(config: Partial<IEHConfig>) {
      if (this.ehVersion) {
        this.pEhConfig = { ...this.pEhConfig, ...config } as IEHConfig;
        KNB.ready(() => {
          KNB.use('eh.config', this.pEhConfig);
        });
      }
    }

    setTitle(title: string) {
      // 在 EH 环境下，transPage 动画过程中设置的标题无效
      if (this.ehVersion) {
        setTimeout(() => {
          document.title = title;
          KNB.ready(() => {
            KNB.setTitle({ title });
          });
        }, 100);
      } else {
        document.title = title;
        KNB.ready(() => {
          KNB.setTitle({ title });
        });
      }
    }

    showNavigationBar() {
      if (this.ehVersion) {
        this.configEH({
          hideNavigationBar: false
        });
      } else {
        KNB.ready(() => {
          KNB.setNavigationBarHidden({ flag: 0 });
        });
      }
    }

    ehShow() {
      if (this.ehVersion) {
        KNB.ready(() => {
          KNB.use('eh.show');
        });
      }
    }

    // 关闭 WebView
    closeWebView(): void {
      // 通过 knb 关闭 webview
      KNB.ready(() => {
        if (this.ehVersion && this.system === 'ios') {
          // close transpage
          try {
            KNB.use('eh.closeTrans');
          } catch (e) {
            KNB.closeWebview({});
          }
        } else {
          KNB.closeWebview({});
        }
      });
    }

    openWebView(url: string, useEH = true): void {
      if (this.webViewOpening) {
        return;
      }

      if (KNB.env.isNative) {
        if (this.ehVersion && useEH) {
          if (this.system !== 'android') {
            toast.show('loading', undefined, { duration: 8000 });
          }
          KNB.use('eh.open', {
            url,
            success: () => toast.hide(),
            fail: () => toast.hide()
          });
        } else {
          KNB.ready(() => {
            KNB.openWebview({ url });
          });
        }
      } else {
        location.href = url;
      }

      this.webViewOpening = true;
      window.setTimeout(() => {
        this.webViewOpening = false;
      }, 300);
    }

    addScrollHandler(handler: IScrollHandler) {
      this.scrollHandlers.push(handler);
      if (this.scrollHandlers.length === 1) {
        this.hookEHScroll();
      }
    }

    removeScrollHandler(handler: IScrollHandler) {
      this.scrollHandlers = this.scrollHandlers.filter((h) => handler !== h);
    }

    readyForTransPage(step: number, useAnimation = true, cb?: () => void) {
      /**
       * 在 iOS TransPage 切换页面的过程中，实际上是把 UIWebView 从一个 View 父节点切换到了另一个 View
       * 但这种操作行为将会导致 UIWebView 中用于适配 iOS11 iPhoneX 的 Constant (safe-area-inset-*) 失效。
       *
       * 通过触发页面的重绘能够使 Constant 重新生效，这里通过页面切换前后快速改变页面的 scaleY 来实现。
       *
       * Bug Platform: iOS11 UIWebView
       */
      const docEl = document.documentElement as HTMLElement;

      if (this.system === 'ios' && this.ehVersion) {
        docEl.style.transform = 'scaleY(1.01)';
      }
      Vue.nextTick(() => {
        if (this.ehVersion) {
          KNB.ready(() => {
            KNB.use('eh.transComplete', { dr: step, useAnimation });
            if (this.system === 'ios') {
              docEl.style.transform = null;
            }
          });
        }
        if (cb) {
          cb();
        }
      });
    }

    transPageGo(step: number, complete?: () => void) {
      if (this.ehVersion) {
        KNB.ready(() => {
          KNB.use('eh.trans', {
            step,
            success: () => {
              if (complete) {
                complete();
              }
            }
          });
        });
      } else {
        if (complete) {
          complete();
        }
      }
    }

    private hookEHScroll() {
      if (this.system === 'ios' && this.ehVersion) {
        window.ehScrollCallback = (pos: IPosition) => {
          this.scrollHandlers.map((handler) => {
            handler(pos);
          });
        };

        KNB.ready(() => {
          KNB.use('eh.event', {
            event: 'onScrollChange',
            callbackName: 'ehScrollCallback'
          });
        });
      } else {
        window.addEventListener(
          'scroll',
          () => {
            this.scrollHandlers.map((handler) => {
              handler({ x: window.scrollX, y: window.scrollY });
            });
          },
          false
        );
      }
    }
  };
}

declare global {
  interface Window {
    ehScrollCallback: (pos: IPosition) => void;
  }
}
